import { FC, useRef, useState } from "react";
import {
  DefaultOfficeForm,
  DefaultOfficeFormRef,
  OfficeFormData,
  EditOfficeFormData,
} from "./DefaultOfficeForm";
import { updateOffice } from "@/api";
import { useSnackbar, VariantType } from "notistack";
import { usePermissions } from "@/hooks/usePermissions";
import LoggingAPI from "@/api/logging";
import { Button } from "@monolith-forensics/monolith-ui";

interface UpdateOfficeFormProps {
  defaultFormData: OfficeFormData;
  onSubmit: () => void;
  onCancel: () => void;
}

export const UpdateOfficeForm: FC<UpdateOfficeFormProps> = ({
  defaultFormData,
  onSubmit,
  onCancel,
}) => {
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const formRef = useRef<DefaultOfficeFormRef>(null);
  const [isFormTouched, setIsFormTouched] = useState(false);

  const handleTouchedChange = (touched: boolean) => {
    setIsFormTouched(touched);
  };

  const handleSubmit = async (
    data: OfficeFormData,
    diffData?: EditOfficeFormData
  ) => {
    try {
      if (diffData && Object.keys(diffData).length > 0) {
        await updateOffice(defaultFormData.office_id!, diffData);

        enqueueSnackbar(`Office ${data?.office_name || ""} updated`, {
          variant: "success" as VariantType,
        });

        LoggingAPI.createLog({
          user_id: currentUser.user_id,
          message: `Updated Office: ${data?.office_name}`,
          event: "office:update",
          object_type: "office",
        });

        // Reset touched state after successful update
        formRef.current?.resetTouched();
        setIsFormTouched(false);
        onSubmit?.();
      } else {
        // No changes to update
        enqueueSnackbar(`No changes to update.`, {
          variant: "warning" as VariantType,
        });
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        `An error occurred while updating office ${
          data?.office_name || ""
        }. Please try again.`,
        { variant: "error" as VariantType }
      );
    }
  };

  const handleButtonSubmit = async () => {
    await formRef.current?.handleSubmit();
  };

  return (
    <>
      <DefaultOfficeForm
        ref={formRef}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        defaultFormData={defaultFormData}
        showButtons={false}
        controlled={true}
        onTouchedChange={handleTouchedChange}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: "15px",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          style={{ marginLeft: "auto" }}
          size="xs"
          disabled={!isFormTouched}
          onClick={handleButtonSubmit}
        >
          Save Office Changes
        </Button>
      </div>
    </>
  );
};
